// reconnectRecovery — resyncs editor state after a reconnect, and keeps a
// fallback for a connection that opens and is then dropped straight away.
//
// NOT how a reboot is recovered from. When the device reboots it re-mints its
// session token and refuses a stale-token handshake with a 401, so onopen
// never fires. The fast-fail detection below counts closes measured from the
// last *successful* connect, so it cannot see that case at all — which is why
// a power-cycled device used to need a page refresh (MDL-384). The token is
// now refreshed before each reconnect attempt, by the URL function the
// connection hands ReconnectingWebSocket (see HttpDeviceConnection._wsUrl and
// the on-device DeviceApi._wsUrl).
//
// What remains here: onStaleToken() still fires if several reconnects each
// close within fastFailWindowMs of opening — a device that accepts and then
// drops the socket — so the caller can reconnect from scratch.
//
// Separately (and on every reconnect, not just the stale-token case),
// onResyncNeeded() is called once the connection is restored, since state
// changes that occurred while disconnected (e.g. a MIDI-driven patch change)
// were missed and need to be re-fetched.
//
// Feed state transitions in from a device's onConnectionStateChanged(state)
// callback, where state is 'connected' | 'reconnecting'.

export function createReconnectRecoveryTracker({
  onStaleToken,
  onResyncNeeded,
  fastFailThreshold = 3,
  fastFailWindowMs = 2000,
} = {}) {
  let lastConnectedAt = null;
  let consecutiveFastFails = 0;
  let needsResync = false;

  return {
    async handleConnectionState(state) {
      if (state === 'connected') {
        lastConnectedAt = Date.now();
        consecutiveFastFails = 0;
        if (needsResync) {
          needsResync = false;
          if (onResyncNeeded) await onResyncNeeded();
        }
      } else if (state === 'reconnecting') {
        needsResync = true;
        if (lastConnectedAt !== null && Date.now() - lastConnectedAt < fastFailWindowMs) {
          consecutiveFastFails++;
          if (consecutiveFastFails >= fastFailThreshold) {
            consecutiveFastFails = 0;
            lastConnectedAt = null;
            // The caller's onStaleToken performs its own full reconnect/resync.
            needsResync = false;
            if (onStaleToken) await onStaleToken();
          }
        } else {
          consecutiveFastFails = 0;
        }
      }
    },
  };
}
